import { useQueryClient } from "@tanstack/react-query";
import type { Root as MdastRoot } from "mdast";
import { useCallback } from "react";
import {
  getGetCustomReportQueryKey,
  getGetReportSectionQueryKey,
  getListCustomReportsByCaseQueryKey,
  useGetReportSection,
  useRegenerateSection,
  useUpdateReportSection,
} from "~/api/custom-reports/custom-reports";
import type {
  Report,
  ReportSection,
  UpdateReportSectionInput,
} from "~/api/model";
import { generateIdempotencyKey } from "./reportUtils";

/** Cached section shape including the (not-yet-generated) `contentMdast`. */
type CachedReportSection = ReportSection & { contentMdast?: MdastRoot };

export const useSectionData = (
  organizationId: string,
  projectId: string,
  reportId: string,
  sectionId: string,
  sectionStatus: string | undefined,
  isExpanded: boolean,
  isVisible: boolean
) => {
  const queryClient = useQueryClient();

  const { data: sectionData, isLoading } = useGetReportSection(
    organizationId,
    projectId,
    reportId,
    sectionId,
    {
      query: {
        enabled:
          isExpanded &&
          isVisible &&
          !!sectionId &&
          !!reportId &&
          sectionStatus === "completed",
      },
    }
  );

  const { mutate: regenerateSection } = useRegenerateSection({
    mutation: {
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: getGetCustomReportQueryKey(
            organizationId,
            projectId,
            reportId
          ),
        });

        const previousReport = queryClient.getQueryData(
          getGetCustomReportQueryKey(organizationId, projectId, reportId)
        );

        queryClient.setQueryData(
          getGetCustomReportQueryKey(organizationId, projectId, reportId),
          (old: Report | undefined) => {
            if (!old?.reportSections) return old;

            return {
              ...old,
              reportSections: old.reportSections.map((section) =>
                section.id === variables.reportSectionId
                  ? { ...section, status: "pending" as const }
                  : section
              ),
            };
          }
        );

        return { previousReport };
      },
      onError: (_err, _variables, context) => {
        if (context?.previousReport) {
          queryClient.setQueryData(
            getGetCustomReportQueryKey(organizationId, projectId, reportId),
            context.previousReport
          );
        }
      },
      onSuccess: async (_data, variables) => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetCustomReportQueryKey(
              organizationId,
              projectId,
              reportId
            ),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetReportSectionQueryKey(
              organizationId,
              projectId,
              variables.reportId,
              variables.reportSectionId
            ),
          }),
          queryClient.invalidateQueries({
            queryKey: getListCustomReportsByCaseQueryKey(
              organizationId,
              projectId
            ),
          }),
        ]);
      },
    },
  });

  const { mutate: updateReportSection } = useUpdateReportSection();

  const handleRegenerate = useCallback(() => {
    if (!sectionId) return;
    regenerateSection({
      organizationId,
      projectId,
      reportId,
      reportSectionId: sectionId,
      data: {
        idempotencyKey: generateIdempotencyKey(),
      },
    });
  }, [regenerateSection, organizationId, projectId, reportId, sectionId]);

  const handleEdit = useCallback(
    async (content: string | MdastRoot): Promise<void> => {
      if (!sectionId) return;
      const contentMdast = typeof content === "string" ? undefined : content;
      const data: UpdateReportSectionInput =
        contentMdast === undefined
          ? { content: content as string }
          : {
              contentMdast: contentMdast as unknown as NonNullable<
                UpdateReportSectionInput["contentMdast"]
              >,
            };
      const sectionKey = getGetReportSectionQueryKey(
        organizationId,
        projectId,
        reportId,
        sectionId
      );
      return new Promise((resolve, reject) => {
        updateReportSection(
          {
            organizationId,
            projectId,
            reportId,
            reportSectionId: sectionId,
            data,
          },
          {
            onSuccess: async () => {
              if (contentMdast !== undefined) {
                queryClient.setQueryData<CachedReportSection>(
                  sectionKey,
                  (old) => (old ? { ...old, contentMdast } : old)
                );
              } else {
                await queryClient.invalidateQueries({ queryKey: sectionKey });
              }
              resolve();
            },
            onError: (error) => reject(error),
          }
        );
      });
    },
    [
      updateReportSection,
      organizationId,
      projectId,
      reportId,
      sectionId,
      queryClient,
    ]
  );

  return {
    sectionData,
    isLoading,
    handleRegenerate,
    handleEdit,
  };
};
